import { store, mentionPattern, matchedEntity } from "../editor/store"
import { groupLabel } from "./inspector"

export const FLOW_COLORS = [
  "#4d97de", "#c15f2e", "#05ab96", "#ac5ca6",
  "#ae931d", "#c75157", "#158eab", "#6da24d"
]

const SURFACE = "#2e3440"
const KIND_TABS = [
  ["all", "All"], ["character", "Characters"], ["location", "Locations"],
  ["thread", "Threads"], ["other", "More"]
]

export function sectionsAtLevel(text, level) {
  const pattern = new RegExp(`^#{1,${level}}\\s+(.+)$`)
  const sections = []
  let current = { label: "Opening", body: [] }
  text.split("\n").forEach((line) => {
    const heading = line.match(pattern)
    if (heading) {
      if (current.body.length || sections.length) sections.push(current)
      current = { label: heading[1].trim(), body: [] }
    } else {
      current.body.push(line)
    }
  })
  sections.push(current)
  return sections.filter((section, index) => index > 0 || section.body.some((l) => l.trim()))
}

export function sectionSeries(text, level) {
  const sections = sectionsAtLevel(text, level)
  const perEntity = new Map()
  sections.forEach((section, index) => {
    for (const match of section.body.join("\n").matchAll(mentionPattern())) {
      const entity = matchedEntity(match)
      if (!entity) continue
      if (!perEntity.has(entity.id)) perEntity.set(entity.id, new Array(sections.length).fill(0))
      perEntity.get(entity.id)[index] += 1
    }
  })
  return {
    columns: sections.map((section) => ({ label: section.label })),
    series: [...perEntity.entries()].map(([id, counts]) => {
      const entity = store.findEntity(id)
      return { id, name: entity.name, kind: entity.kind, counts, total: counts.reduce((a, b) => a + b, 0) }
    })
  }
}

export class FlowPaneController {
  constructor({ getText, flowUrl, options, onOptionsChanged }) {
    this.getText = getText
    this.flowUrl = flowUrl
    this.state = {
      mode: options.mode || "chapters",
      level: options.level || 2,
      assignments: options.assignments || {},
      selected: new Set(options.selected || []),
      table: false
    }
    this.onOptionsChanged = onOptionsChanged
    this.chapterData = null
  }

  async data() {
    if (this.state.mode === "sections") return sectionSeries(this.getText(), this.state.level)
    if (!this.chapterData) {
      const payload = await (await fetch(this.flowUrl, { headers: { Accept: "application/json" } })).json()
      this.chapterData = {
        columns: payload.documents.map((doc) => ({ label: doc.title })),
        series: payload.series
      }
    }
    return this.chapterData
  }

  colorFor(entityId) {
    const key = String(entityId)
    if (!(key in this.state.assignments)) {
      const used = new Set(Object.values(this.state.assignments))
      const free = FLOW_COLORS.findIndex((_, index) => !used.has(index))
      if (free === -1) return null
      this.state.assignments[key] = free
    }
    return FLOW_COLORS[this.state.assignments[key]]
  }

  persist() {
    const snapshot = {
      mode: this.state.mode,
      level: this.state.level,
      assignments: this.state.assignments,
      selected: [...this.state.selected]
    }
    this.lastPersisted = JSON.stringify(snapshot)
    this.onOptionsChanged(snapshot)
  }

  persistIfDirty() {
    const current = JSON.stringify({
      mode: this.state.mode,
      level: this.state.level,
      assignments: this.state.assignments,
      selected: [...this.state.selected]
    })
    if (current !== this.lastPersisted) this.persist()
  }

  async build() {
    const pane = document.createElement("section")
    pane.className = "pane pane-wide"
    pane.tabIndex = -1
    pane.dataset.workspaceTarget = "pane"
    pane.dataset.flow = "true"
    this.pane = pane

    const header = document.createElement("header")
    header.className = "px-5 pt-5 pb-3 border-b border-nord3/40 flex items-center gap-3"
    const title = document.createElement("h2")
    title.className = "text-xl font-bold text-nord6 flex-1"
    title.textContent = "Flow"
    header.appendChild(title)

    this.modeSwitch = document.createElement("div")
    this.modeSwitch.className = "flex gap-1"
    ;[["chapters", "Chapters"], ["sections", "Sections"]].forEach(([mode, label]) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "flow-tab"
      button.dataset.mode = mode
      button.textContent = label
      button.addEventListener("click", () => {
        this.state.mode = mode
        this.persist()
        this.render()
      })
      this.modeSwitch.appendChild(button)
    })
    header.appendChild(this.modeSwitch)

    this.tableToggle = document.createElement("button")
    this.tableToggle.type = "button"
    this.tableToggle.className = "flow-tab"
    this.tableToggle.textContent = "Table"
    this.tableToggle.addEventListener("click", () => {
      this.state.table = !this.state.table
      this.render()
    })
    header.appendChild(this.tableToggle)
    pane.appendChild(header)

    this.chartHost = document.createElement("div")
    this.chartHost.className = "px-4 pt-4"
    pane.appendChild(this.chartHost)

    this.legendHost = document.createElement("div")
    this.legendHost.className = "px-4 py-3"
    pane.appendChild(this.legendHost)

    this.kindFilter = "all"
    await this.render()
    return pane
  }

  async render() {
    const data = await this.data()
    if (!this.state.selected.size) {
      data.series
        .slice()
        .sort((a, b) => b.total - a.total)
        .slice(0, 4)
        .forEach((line) => this.state.selected.add(line.id))
    }
    const active = data.series.filter((line) => this.state.selected.has(line.id) && line.total > 0)

    this.modeSwitch.querySelectorAll("button").forEach((button) => {
      button.classList.toggle("is-active", button.dataset.mode === this.state.mode)
    })
    this.tableToggle.classList.toggle("is-active", this.state.table)

    this.chartHost.replaceChildren(
      this.state.table ? this.renderTable(data, active) : this.renderChart(data, active)
    )
    this.renderLegend(data)
    this.persistIfDirty()
  }

  renderChart({ columns }, active) {
    if (!columns.length || !active.length) {
      const empty = document.createElement("p")
      empty.className = "py-8 text-center text-sm text-nord4/50 italic"
      empty.textContent = "The flow begins once someone enters the tale."
      return empty
    }

    const width = 440
    const height = 240
    const margin = { top: 12, right: 76, bottom: 34, left: 30 }
    const innerW = width - margin.left - margin.right
    const innerH = height - margin.top - margin.bottom
    const maxY = Math.max(1, ...active.flatMap((line) => line.counts))
    const stepX = columns.length > 1 ? innerW / (columns.length - 1) : 0
    const x = (index) => margin.left + (columns.length > 1 ? index * stepX : innerW / 2)
    const y = (value) => margin.top + innerH - (value / maxY) * innerH

    const svgNS = "http://www.w3.org/2000/svg"
    const svg = document.createElementNS(svgNS, "svg")
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`)
    svg.setAttribute("class", "flow-chart")

    const ticks = Math.min(4, maxY)
    for (let t = 0; t <= ticks; t++) {
      const value = Math.round((maxY / ticks) * t)
      const line = document.createElementNS(svgNS, "line")
      line.setAttribute("x1", margin.left)
      line.setAttribute("x2", width - margin.right)
      line.setAttribute("y1", y(value))
      line.setAttribute("y2", y(value))
      line.setAttribute("class", "flow-grid")
      svg.appendChild(line)
      const label = document.createElementNS(svgNS, "text")
      label.setAttribute("x", margin.left - 6)
      label.setAttribute("y", y(value) + 3)
      label.setAttribute("text-anchor", "end")
      label.setAttribute("class", "flow-axis-label")
      label.textContent = value
      svg.appendChild(label)
    }

    columns.forEach((column, index) => {
      const label = document.createElementNS(svgNS, "text")
      label.setAttribute("x", x(index))
      label.setAttribute("y", height - margin.bottom + 16)
      label.setAttribute("text-anchor", "middle")
      label.setAttribute("class", "flow-axis-label")
      label.textContent = column.label.length > 9 ? `${column.label.slice(0, 8)}…` : column.label
      const full = document.createElementNS(svgNS, "title")
      full.textContent = column.label
      label.appendChild(full)
      svg.appendChild(label)
    })

    this.seriesGroups = new Map()
    active.forEach((line) => {
      const color = this.colorFor(line.id)
      if (!color) return
      const group = document.createElementNS(svgNS, "g")
      group.setAttribute("class", "flow-series")

      const points = line.counts.map((value, index) => `${x(index)},${y(value)}`).join(" ")
      const path = document.createElementNS(svgNS, "polyline")
      path.setAttribute("points", points)
      path.setAttribute("fill", "none")
      path.setAttribute("stroke", color)
      path.setAttribute("stroke-width", "2")
      path.setAttribute("stroke-linejoin", "round")
      group.appendChild(path)

      line.counts.forEach((value, index) => {
        const dot = document.createElementNS(svgNS, "circle")
        dot.setAttribute("cx", x(index))
        dot.setAttribute("cy", y(value))
        dot.setAttribute("r", "4")
        dot.setAttribute("fill", color)
        dot.setAttribute("stroke", SURFACE)
        dot.setAttribute("stroke-width", "2")
        const tip = document.createElementNS(svgNS, "title")
        tip.textContent = `${line.name} — ${columns[index].label}: ${value}`
        dot.appendChild(tip)
        group.appendChild(dot)
      })

      group.addEventListener("mouseenter", () => this.setHover(line.id))
      group.addEventListener("mouseleave", () => this.clearHover())
      this.seriesGroups.set(line.id, group)
      svg.appendChild(group)
    })

    if (active.length <= 4) {
      const lastIndex = columns.length - 1
      const labels = active
        .map((line) => ({ name: line.name, y: y(line.counts[lastIndex]) + 3 }))
        .sort((a, b) => a.y - b.y)
      labels.forEach((label, index) => {
        if (index > 0 && label.y - labels[index - 1].y < 11) {
          label.y = labels[index - 1].y + 11
        }
      })
      labels.forEach((label) => {
        const text = document.createElementNS(svgNS, "text")
        text.setAttribute("x", x(lastIndex) + 8)
        text.setAttribute("y", label.y)
        text.setAttribute("class", "flow-direct-label")
        text.textContent = label.name.length > 12 ? `${label.name.slice(0, 11)}…` : label.name
        svg.appendChild(text)
      })
    }

    return svg
  }

  renderTable({ columns }, active) {
    const wrap = document.createElement("div")
    wrap.className = "overflow-x-auto"
    const table = document.createElement("table")
    table.className = "flow-table"
    const head = table.createTHead().insertRow()
    head.appendChild(document.createElement("th"))
    columns.forEach((column) => {
      const th = document.createElement("th")
      th.textContent = column.label
      head.appendChild(th)
    })
    const body = table.createTBody()
    active.forEach((line) => {
      const row = body.insertRow()
      const name = document.createElement("th")
      name.scope = "row"
      name.textContent = line.name
      row.appendChild(name)
      line.counts.forEach((value) => {
        const cell = row.insertCell()
        cell.textContent = value || "·"
      })
    })
    wrap.appendChild(table)
    return wrap
  }

  renderLegend(data) {
    this.legendHost.replaceChildren()

    const tabs = document.createElement("div")
    tabs.className = "flex gap-1 pb-2"
    KIND_TABS.forEach(([kind, label]) => {
      const tab = document.createElement("button")
      tab.type = "button"
      tab.className = "flow-tab"
      tab.classList.toggle("is-active", this.kindFilter === kind)
      tab.textContent = label
      tab.addEventListener("click", () => {
        this.kindFilter = kind
        this.render()
      })
      tabs.appendChild(tab)
    })
    this.legendHost.appendChild(tabs)

    const rows = document.createElement("div")
    rows.className = "space-y-0.5 max-h-48 overflow-y-auto"
    const visible = data.series.filter((line) => {
      if (this.kindFilter === "all") return true
      if (this.kindFilter === "other") return !["character", "location", "thread"].includes(line.kind)
      return line.kind === this.kindFilter
    })

    visible
      .slice()
      .sort((a, b) => b.total - a.total)
      .forEach((line) => {
        const row = document.createElement("button")
        row.type = "button"
        row.className = "flow-legend-row"
        const on = this.state.selected.has(line.id)
        row.classList.toggle("is-active", on)

        const chip = document.createElement("span")
        chip.className = "flow-chip"
        if (on) {
          const color = this.colorFor(line.id)
          if (color) chip.style.background = color
        }
        row.appendChild(chip)

        const name = document.createElement("span")
        name.className = "flex-1 truncate text-sm text-nord4"
        name.textContent = line.name
        row.appendChild(name)

        const kind = document.createElement("span")
        kind.className = "text-[10px] uppercase tracking-widest text-nord4/40"
        kind.textContent = line.kind
        row.appendChild(kind)

        const total = document.createElement("span")
        total.className = "text-xs text-nord4/50 tabular-nums"
        total.textContent = `${line.total}×`
        row.appendChild(total)

        row.addEventListener("click", () => {
          if (on) {
            this.state.selected.delete(line.id)
          } else {
            if (this.state.selected.size >= FLOW_COLORS.length) return
            this.state.selected.add(line.id)
          }
          this.persist()
          this.render()
        })
        if (on) {
          row.addEventListener("mouseenter", () => this.setHover(line.id))
          row.addEventListener("mouseleave", () => this.clearHover())
        }
        rows.appendChild(row)
      })
    this.legendHost.appendChild(rows)

    if (this.state.mode === "sections") {
      const levels = document.createElement("div")
      levels.className = "flex items-center gap-1 pt-2 text-[11px] text-nord4/50"
      const label = document.createElement("span")
      label.textContent = "Showing"
      levels.appendChild(label)
      const text = this.getText()
      ;[1, 2, 3].forEach((level) => {
        const headings = sectionsAtLevel(text, level)
          .filter((section) => section.label !== "Opening")
          .map((section) => ({ title: section.label }))
        const button = document.createElement("button")
        button.type = "button"
        button.className = "flow-tab"
        button.classList.toggle("is-active", this.state.level === level)
        button.textContent = (headings.length && groupLabel(headings)) || `H${level}`
        button.addEventListener("click", () => {
          this.state.level = level
          this.persist()
          this.render()
        })
        levels.appendChild(button)
      })
      this.legendHost.appendChild(levels)
    }
  }

  setHover(entityId) {
    this.seriesGroups?.forEach((group, id) => {
      group.classList.toggle("is-dim", id !== entityId)
    })
  }

  clearHover() {
    this.seriesGroups?.forEach((group) => group.classList.remove("is-dim"))
  }

  focusEntity(entityId) {
    if (!this.state.selected.has(entityId) && this.state.selected.size < FLOW_COLORS.length) {
      this.state.selected.add(entityId)
      this.persist()
    }
    this.render()
  }
}
